import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Button } from "../../buttons/Button/Button";
import { Box } from "../../boxes/Box/Box";

import {
  checkIsFiveMinutesBefore,
  getDateView,
  getMonthName,
  getOrdinal,
  getTimeAsString,
} from "../../../utils";

import "./consultation-dashboard.scss";

const AMAZON_S3_BUCKET = `${import.meta.env.VITE_AMAZON_S3_BUCKET}`;

/**
 * ConsultationDashboard
 *
 * ConsultationDashboard component
 *
 * @return {jsx}
 */
export const ConsultationDashboard = ({
  classes,
  consultation,
  handleJoin,
  handleEdit,
  handleAcceptSuggestion,
  handleSchedule,
  t,
  toast,
}) => {
  const currencySymbol = localStorage.getItem("currency_symbol");
  const {
    providerName,
    timestamp,
    image,
    status,
    price,
    campaignId,
    sponsorImage,
    couponPrice,
  } = consultation || {};
  const imageUrl = AMAZON_S3_BUCKET + "/" + (image || "default");

  const isBookedWithCoupon = couponPrice || campaignId;

  // Only scheduled (or already finished) consultations can be joined, e.g. a pending one is still being booked
  const isJoinable = status === "scheduled" || status === "finished";
  const isLive =
    isJoinable &&
    checkIsFiveMinutesBefore(timestamp, consultation?.durationMinutes);
  const withOrganization = !!consultation?.organizationId;

  const startDate = new Date(timestamp);
  const ordinal = getOrdinal(startDate?.getDate());
  const dateText = `${getDateView(startDate).slice(0, 2)}${t(ordinal)} ${t(
    getMonthName(startDate).toLowerCase()
  )}`;

  const timeText = startDate ? getTimeAsString(startDate) : "";
  return (
    <Box
      shadow={1}
      classes={["consultation-dashboard", classNames(classes)].join(" ")}
    >
      {consultation ? (
        <div className="consultation-dashboard__content">
          <div className="consultation-dashboard__content__date">
            {isLive ? (
              <p className="small-text now-text">{t("live_label")}</p>
            ) : (
              <p className="small-text">{`${dateText} ${timeText}`}</p>
            )}
            <div
              className={`consultation-dashboard__content__date__price__badge ${
                consultation.price > 0 && !campaignId
                  ? "consultation-dashboard__content__date__price__badge--paid"
                  : "consultation-dashboard__content__date__price__badge--free"
              }`}
            >
              {campaignId && sponsorImage ? (
                <img
                  className="consultation-dashboard__content__date__price__badge__sponsor-image"
                  src={AMAZON_S3_BUCKET + "/" + sponsorImage}
                  alt="sponsor"
                />
              ) : null}
              {withOrganization ? (
                <img
                  className="consultation-dashboard__content__date__price__badge__sponsor-image"
                  src={AMAZON_S3_BUCKET + "/" + "organization"}
                  alt="ogranization"
                />
              ) : null}
              <p className="small-text">
                {isBookedWithCoupon
                  ? t("coupon")
                  : consultation.price > 0
                  ? `${consultation.price}${currencySymbol || ""}`
                  : t("free")}
              </p>
            </div>
          </div>
          <div className="consultation-dashboard__content__provider-container">
            <img src={imageUrl} className="provider-image" />
            <p className="text">{providerName}</p>
          </div>
          {status === "suggested" ? (
            <Button
              type="primary"
              label={t("accept_button_label")}
              size="sm"
              onClick={() =>
                handleAcceptSuggestion(consultation.consultationId, price)
              }
            />
          ) : isLive ? (
            <Button
              label={t("join_button_label")}
              color="purple"
              size="sm"
              classes="consultation-dashboard__button"
              onClick={() => handleJoin(consultation)}
            />
          ) : (
            <div className="consultation-dashboard__button-container">
              <div
                className="consultation-dashboard__disabled-button-wrapper"
                onClick={() => toast.info(t("join_button_label_tooltip"))}
              >
                <Button
                  label={t("join_button_label")}
                  size="sm"
                  color="purple"
                  disabled
                />
              </div>
              <Button
                label={t("change_button_label")}
                type="secondary"
                size="sm"
                color="purple"
                onClick={() => handleEdit(consultation)}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="consultation-dashboard__no-consultation">
          <p className="small-text no-booking-text">{t("no_consultations")}</p>
          <Button
            size="sm"
            label={t("schedule_button_label")}
            color="purple"
            onClick={handleSchedule}
          />
        </div>
      )}
    </Box>
  );
};

ConsultationDashboard.propTypes = {
  /**
   * Additional classes to be added to the ConsultationDashboard
   * */
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),

  /**
   * The translation function
   */
  t: PropTypes.func,
};

ConsultationDashboard.defaultProps = {
  providerName: "",
  consultationDate: "",
  isLive: false,
};
